import { useMemo } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { format } from 'date-fns';
import { z } from 'zod';

import {
  BaseOriginAlertType,
  HeaderScanType,
  OriginAlertsPageType,
  OriginAlertStatus,
  OriginAlertType,
  OriginHeadersPageType,
  OriginScriptsPageType,
  OriginScriptType,
  OriginsPageType,
  OriginType,
} from '@components/features/Compliance/types';
import { useRecentActions } from '@components/features/Overview/QuickLinks/hook';
import { directApi } from '@services/apiService';
import { useStrongParams } from '@utils/params';

export const ORIGIN_ALERT_PAGE_SIZE = 50;

export const originIndexParamsSchema = z.object({
  teamUuid: z.string(),
  appUuid: z.string(),
});
type OriginIndexParams = z.infer<typeof originIndexParamsSchema>;

export const originPageParamsSchema = z.object({
  teamUuid: z.string(),
  appUuid: z.string(),
  originUuid: z.string(),
});
type OriginPageParams = z.infer<typeof originPageParamsSchema>;

type SortDirection = 'asc' | 'desc';

export interface UseOriginInfiniteAlertsParams {
  status?: OriginAlertStatus[];
  sort?: { [key in keyof BaseOriginAlertType]?: SortDirection };
  scriptUuid?: string;
}

export interface UseOriginPaginatedAlertsParams
  extends UseOriginInfiniteAlertsParams {
  page: number;
}

export interface UseOriginPaginatedHeadersParams {
  page: number;
}

export interface UseOriginPaginatedScriptsParams {
  page: number;
  sort?: { updatedAt?: SortDirection };
}

export interface UseOriginScriptParams {
  scriptUuid: string;
}

export interface UseOriginHeaderParams {
  headerUuid: string;
}

export interface UseOriginAlertParams {
  alertUuid: string;
}

export interface UseUpdateAlertParams {
  alertUuid: string;
}

export interface UseBulkUpdateAlertsVariables {
  scriptUuid: string;
  status: 'approved' | 'denied';
  justification: string | null;
}

export interface UseUpdateAlertVariables {
  status: 'approved' | 'denied';
  justification: string | null;
}

export interface UseCreateOriginVariables {
  name: string | null;
  origin: string;
}

export interface UseUpdateOriginVariables {
  origin: string;
  name: string | null;
}

export interface UseExportIssuesParams {
  originUuid: string;
}

export interface UseExportIssuesVariables {
  startDate: Date;
  endDate: Date;
  alertType?: 'header' | 'script';
  alertStatus?: ('new' | 'approved' | 'denied')[];
  alertSeverity?: ('info' | 'medium' | 'high' | 'critical')[];
}

export const ORIGIN_QUERY_FILTERS = {
  allAlerts(params: UseOriginInfiniteAlertsParams & OriginPageParams) {
    return {
      queryKey: [
        'teams',
        params.teamUuid,
        'apps',
        params.appUuid,
        'origins',
        params.originUuid,
        'alerts',
        'all',
      ],
    };
  },

  allScripts(
    params: Omit<UseOriginPaginatedScriptsParams, 'page'> & OriginPageParams
  ) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'scripts',
      'all',
    ];
  },
};

export const ORIGIN_QUERY_KEYS = {
  allOrigins(params: OriginIndexParams) {
    return ['teams', params.teamUuid, 'apps', params.appUuid, 'origins'];
  },

  allAlerts(params: UseOriginInfiniteAlertsParams & OriginPageParams) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'alerts',
      'all',
      params.status?.join(','),
      Object.entries(params.sort ?? {})
        .map(([key, direction]) => `${key}:${direction}`)
        .join(',') || undefined,
      params.scriptUuid ? `scriptUuid:${params.scriptUuid}` : undefined,
    ];
  },

  allAlertsPaginated(
    params: UseOriginPaginatedAlertsParams & OriginPageParams
  ) {
    return [...ORIGIN_QUERY_KEYS.allAlerts(params), 'page', params.page];
  },

  countAlerts(params: OriginPageParams) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'alerts',
      'count',
    ];
  },

  alert(params: UseOriginAlertParams & OriginPageParams) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'alerts',
      params.alertUuid,
    ];
  },

  allScriptsPaginated(
    params: UseOriginPaginatedScriptsParams & OriginPageParams
  ) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'scripts',
      'all',
      Object.entries(params.sort ?? {})
        .map(([key, direction]) => `${key}:${direction}`)
        .join(',') || undefined,
      'page',
      params.page,
    ];
  },

  script(params: UseOriginScriptParams & OriginPageParams) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'scripts',
      params.scriptUuid,
    ];
  },

  allHeadersPaginated(
    params: UseOriginPaginatedHeadersParams & OriginPageParams
  ) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'headers',
      'all',
      'page',
      params.page,
    ];
  },

  header(params: UseOriginHeaderParams & OriginPageParams) {
    return [
      'teams',
      params.teamUuid,
      'apps',
      params.appUuid,
      'origins',
      params.originUuid,
      'headers',
      params.headerUuid,
    ];
  },
};

export const ORIGIN_MUTATION_KEYS = {
  updateAlert(params: UseUpdateAlertParams) {
    return ['updateAlert', params.alertUuid];
  },

  exportIssues(params: UseExportIssuesParams) {
    return ['exportIssues', params.originUuid];
  },
};

export function useOrigins() {
  const params = useStrongParams(originIndexParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
    queryFn: async () => {
      const response = await directApi.get<OriginsPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins`
      );

      return response.json();
    },
  });
}

export function useOriginAlertsCount() {
  const params = useStrongParams(originPageParamsSchema);
  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.countAlerts(params),
    queryFn: async () => {
      const response = await directApi.get<OriginAlertsPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts`
      );
      const data = await response.json();
      return data.total;
    },
  });
}

export function useOriginInfiniteAlerts(
  { status, sort, scriptUuid }: UseOriginInfiniteAlertsParams,
  options?: { enabled?: boolean }
) {
  const params = useStrongParams(originPageParamsSchema);
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: ORIGIN_QUERY_KEYS.allAlerts({
      ...params,
      status,
      sort,
      scriptUuid,
    }),
    queryFn: async ({ pageParam = 0 }) => {
      const searchParams: Record<string, string | number> = {
        page: pageParam,
        pageSize: ORIGIN_ALERT_PAGE_SIZE,
      };
      if (sort) {
        searchParams.sort = Object.entries(sort)
          .map(([key, direction]) => `${key}:${direction}`)
          .join(',');
      }
      if (status) {
        searchParams.status = status.join(',');
      }
      if (scriptUuid) {
        searchParams.scriptUuid = scriptUuid;
      }

      const response = await directApi.get<OriginAlertsPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts`,
        {
          searchParams,
        }
      );
      const data = await response.json();

      // Pre-warm query cache for each alert
      data.data.forEach((alert) => {
        queryClient.setQueryData(
          ORIGIN_QUERY_KEYS.alert({ ...params, alertUuid: alert.uuid }),
          alert
        );
      });

      return data;
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: options?.enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Reset to only first page whenever the query is re-mounted (a.k.a., navigate away and back)
    refetchOnMount(query) {
      queryClient.setQueryData(
        query.queryKey,
        (data: InfiniteData<OriginAlertsPageType> | undefined) => {
          if (!data) return undefined;

          return {
            pages: data.pages.slice(0, 1),
            pageParams: data.pageParams.slice(0, 1),
          };
        }
      );
      return true;
    },
  });
}

export function useOriginPaginatedAlerts({
  status,
  sort,
  scriptUuid,
  page,
}: UseOriginPaginatedAlertsParams) {
  const params = useStrongParams(originPageParamsSchema);
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.allAlertsPaginated({
      ...params,
      status,
      sort,
      page,
      scriptUuid,
    }),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = {
        page: page - 1,
        pageSize: ORIGIN_ALERT_PAGE_SIZE,
      };
      if (sort) {
        searchParams.sort = Object.entries(sort)
          .map(([key, direction]) => `${key}:${direction}`)
          .join(',');
      }
      if (status) {
        searchParams.status = status.join(',');
      }
      if (scriptUuid) {
        searchParams.scriptUuid = scriptUuid;
      }

      const response = await directApi.get<OriginAlertsPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts`,
        {
          searchParams,
        }
      );
      const data = await response.json();

      // Pre-warm query cache for each alert
      data.data.forEach((alert) => {
        queryClient.setQueryData(
          ORIGIN_QUERY_KEYS.alert({ ...params, alertUuid: alert.uuid }),
          alert
        );
      });

      return data;
    },
  });
}

export function useOriginAlerts(alertUuids: string[]) {
  const params = useStrongParams(originPageParamsSchema);

  const queries = useQueries({
    queries: alertUuids.map((alertUuid) => {
      return {
        queryKey: ORIGIN_QUERY_KEYS.alert({ ...params, alertUuid }),
        queryFn: async () => {
          const response = await directApi.get<OriginAlertType>(
            `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts/${alertUuid}`
          );
          return response.json();
        },
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      };
    }),
  });

  return useMemo(
    () => queries.map(({ data }) => data).filter(Boolean) as OriginAlertType[],
    [queries]
  );
}

export function useOrigin() {
  const originsQuery = useOrigins();
  const params = useStrongParams(originPageParamsSchema);

  const origin = useMemo(() => {
    const origins = originsQuery.data?.origins ?? [];
    const match = origins.find((origin) => origin.uuid === params.originUuid);
    return match ?? null;
  }, [originsQuery.data, params.originUuid]);

  return new Proxy(originsQuery, {
    get(target, prop, receiver) {
      if (prop === 'data') {
        return origin;
      } else {
        return Reflect.get(target, prop, receiver);
      }
    },
  }) as UseQueryResult<OriginType | null, Error>;
}

export function useOriginAlert({ alertUuid }: UseOriginAlertParams) {
  const params = useStrongParams(originPageParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.alert({ ...params, alertUuid }),
    queryFn: async () => {
      const response = await directApi.get<OriginAlertType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts/${alertUuid}`
      );
      return response.json();
    },
    enabled: Boolean(alertUuid),
  });
}

export function useCreateOriginMutation() {
  const client = useQueryClient();
  const params = useStrongParams(originIndexParamsSchema);

  return useMutation({
    mutationFn: async (variables: UseCreateOriginVariables) => {
      const response = await directApi.post<OriginType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins`,
        {
          json: {
            name: variables.name || variables.origin,
            origin: variables.origin,
          },
        }
      );
      return response.json();
    },
    onSuccess: (origin) => {
      client.setQueryData<OriginsPageType>(
        ORIGIN_QUERY_KEYS.allOrigins(params),
        (result) => {
          if (!result) return undefined;
          return { ...result, origins: [...result.origins, origin] };
        }
      );

      // Invalidate all origins
      void client.invalidateQueries({
        queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
      });
    },
  });
}

export function useBulkUpdateAlertsMutation() {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);
  return useMutation({
    mutationFn: async (variables: UseBulkUpdateAlertsVariables) => {
      const json: Record<string, string> = {
        status: variables.status,
        scriptUuid: variables.scriptUuid,
      };
      if (variables.justification) {
        json.justification = variables.justification;
      }
      await directApi.patch<OriginAlertType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/scripts/bulk`,
        { json }
      );
    },
    onSuccess: () => {
      // Invalidate script
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_FILTERS.allScripts(params),
      });

      // Invalidate all alerts
      void queryClient.invalidateQueries(
        ORIGIN_QUERY_FILTERS.allAlerts(params)
      );

      // Invalidate origin
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
      });
    },
  });
}

export function useUpdateAlertMutation({ alertUuid }: UseUpdateAlertParams) {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);

  return useMutation({
    mutationKey: ORIGIN_MUTATION_KEYS.updateAlert({ alertUuid }),
    mutationFn: async (variables: UseUpdateAlertVariables) => {
      const json: Record<string, string> = { status: variables.status };
      if (variables.justification) {
        json.justification = variables.justification;
      }

      const response = await directApi.patch<OriginAlertType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/alerts/${alertUuid}`,
        { json }
      );
      return response.json();
    },
    onSuccess: (alert) => {
      // Set the alert to the new value
      queryClient.setQueryData(
        ORIGIN_QUERY_KEYS.alert({ ...params, alertUuid }),
        alert
      );

      // Invalidate script
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_FILTERS.allScripts(params),
      });

      // Invalidate all alerts
      void queryClient.invalidateQueries(
        ORIGIN_QUERY_FILTERS.allAlerts(params)
      );

      // Invalidate origin
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
      });
    },
  });
}

export function useOriginPaginatedScripts({
  page,
  sort,
}: UseOriginPaginatedScriptsParams) {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.allScriptsPaginated({ ...params, sort, page }),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = {
        page: page - 1,
        pageSize: ORIGIN_ALERT_PAGE_SIZE,
      };
      if (sort?.updatedAt) {
        searchParams.sort = `updatedAt:${sort.updatedAt}`;
      }
      const response = await directApi.get<OriginScriptsPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/scripts`,
        { searchParams }
      );
      const data = await response.json();

      // Pre-warm query cache for each script
      data.data.forEach((script) => {
        queryClient.setQueryData(
          ORIGIN_QUERY_KEYS.script({
            ...params,
            scriptUuid: script.scriptUuid,
          }),
          script
        );
      });

      return data;
    },
  });
}

export function useOriginPaginatedHeaders({
  page,
}: UseOriginPaginatedHeadersParams) {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.allHeadersPaginated({ ...params, page }),
    queryFn: async () => {
      const response = await directApi.get<OriginHeadersPageType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/headers`,
        {
          searchParams: {
            page: page - 1,
            pageSize: ORIGIN_ALERT_PAGE_SIZE,
          },
        }
      );

      const data = await response.json();

      // Pre-warm query cache for each header
      data.data.forEach((header) => {
        queryClient.setQueryData(
          ORIGIN_QUERY_KEYS.header({ ...params, headerUuid: header.uuid }),
          header
        );
      });

      return data;
    },
  });
}

export function useOriginScript({ scriptUuid }: UseOriginScriptParams) {
  const params = useStrongParams(originPageParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.script({ ...params, scriptUuid }),
    queryFn: async () => {
      const response = await directApi.get<OriginScriptType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/scripts/${scriptUuid}`
      );
      return response.json();
    },
    enabled: Boolean(scriptUuid),
  });
}

export function useOriginHeader({ headerUuid }: UseOriginHeaderParams) {
  const params = useStrongParams(originPageParamsSchema);

  return useQuery({
    queryKey: ORIGIN_QUERY_KEYS.header({ ...params, headerUuid }),
    queryFn: async () => {
      const response = await directApi.get<HeaderScanType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}/headers/${headerUuid}`
      );
      return response.json();
    },
    enabled: Boolean(headerUuid),
  });
}

export function useUpdateOriginMutation() {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);

  return useMutation({
    mutationFn: async (variables: UseUpdateOriginVariables) => {
      const response = await directApi.patch<OriginType>(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}`,
        { json: variables }
      );
      return response.json();
    },
    onSuccess: () => {
      // Invalidate origin
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
      });
    },
  });
}

export function useDeleteOriginMutation() {
  const queryClient = useQueryClient();
  const params = useStrongParams(originPageParamsSchema);
  const recents = useRecentActions();

  return useMutation({
    mutationFn: async () => {
      await directApi.delete(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/origins/${params.originUuid}`
      );
    },
    onSuccess: () => {
      recents.remove({ type: 'origin', id: params.originUuid });
      // Invalidate all origins
      void queryClient.invalidateQueries({
        queryKey: ORIGIN_QUERY_KEYS.allOrigins(params),
      });
    },
  });
}

export function useExportIssuesMutation({ originUuid }: UseExportIssuesParams) {
  const params = useStrongParams(originIndexParamsSchema);

  return useMutation({
    mutationKey: ORIGIN_MUTATION_KEYS.exportIssues({ originUuid }),
    mutationFn: async (variables: UseExportIssuesVariables) => {
      const json: Record<string, string | string[]> = {
        startDate: format(variables.startDate, 'yyyy-MM-dd'),
        endDate: format(variables.endDate, 'yyyy-MM-dd'),
        originUuids: [originUuid],
      };
      if (variables.alertType) {
        json.alertType = variables.alertType;
      }
      if (variables.alertSeverity) {
        json.alertSeverity = variables.alertSeverity;
      }
      if (variables.alertStatus) {
        json.alertStatus = variables.alertStatus;
      }

      await directApi.post(
        `teams/${params.teamUuid}/apps/${params.appUuid}/page-protection/export/alerts`,
        { json }
      );
    },
  });
}
